/**
 * Admin — Charter Marketplace dashboard
 *
 * Live operator view of every charter request in the platform:
 *
 *   • KPI strip — open / awaiting-pick / assigned / completed-today
 *     plus 24h and 30d GMV.
 *   • Status filter chips.
 *   • Request rows showing route, capacity, bid count, lowest bid (if
 *     visible), assigned driver (if any), and a force-cancel action.
 *
 * Backed by `admin_list_charter_requests` + `admin_force_cancel_charter`
 * RPCs. Realtime-driven so the page reflects mobile activity instantly.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'wouter';
import { toast } from 'sonner';
import {
  Bus,
  RefreshCw,
  Trash2,
  AlertTriangle,
  Clock,
  Users,
  MapPin,
  Banknote,
  ShieldCheck,
  ShieldX,
  ShieldAlert,
  CheckCircle2,
  XCircle,
  UserCheck,
  FileText,
  FileCheck,
  CalendarDays,
  ExternalLink,
} from 'lucide-react';

import { supabase } from '@/lib/supabase';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';

/**
 * Snapshot of a charter driver's in-flight commitments, returned by
 * `admin_get_charter_driver_commitments`. Used to power the
 * Pause/Revoke confirmation modal so admins see the consequences of
 * a destructive action before triggering it.
 */
interface CharterDriverCommitments {
  ok: true;
  live_count: number;
  scheduled_count: number;
  active_bids_count: number;
  live: {
    assignment_id: string;
    request_id: string;
    status: string;
    pickup_label: string;
    dropoff_label: string;
    requested_pickup_at: string;
    passenger: { full_name: string | null; phone_number: string | null };
  }[];
  scheduled: {
    assignment_id: string;
    request_id: string;
    pickup_label: string;
    dropoff_label: string;
    requested_pickup_at: string;
    accepted_amount_xcd: number;
    passenger: { full_name: string | null; phone_number: string | null };
  }[];
}

/** What kind of destructive action the modal is gating. */
type DestructiveAction = 'pause' | 'revoke';

interface DestructiveDialogState {
  driverId: string;
  driverName: string;
  action: DestructiveAction;
  commitments: CharterDriverCommitments | null;
  loadingPreview: boolean;
  previewError: string | null;
  reason: string;
  days: number;
  busy: boolean;
}

interface CharterAdminRequest {
  id: string;
  status: string;
  created_at: string;
  requested_pickup_at: string;
  bids_close_at: string;
  bids_revealed_at: string | null;
  assigned_at: string | null;
  completed_at: string | null;
  canceled_at: string | null;
  cancel_reason: string | null;
  pickup_label: string;
  dropoff_label: string;
  passenger_count: number;
  vehicle_class: string;
  budget_ceiling_xcd: number | null;
  festival_id: string | null;
  passenger: {
    id: string;
    full_name: string | null;
    phone_number: string | null;
    profile_picture_url: string | null;
  };
  live_bid_count: number;
  total_bid_count: number;
  lowest_bid_xcd: number | null;
  // Tourism priority. When TRUE the passenger asked for a DDA /
  // NISE-certified driver and the request was visible only to
  // certified drivers for the first 60 seconds. `general_fanout_at`
  // is when the rest of the eligible pool got it.
  prefer_tourism_certified?: boolean;
  certified_fanout_at?: string | null;
  general_fanout_at?: string | null;
  assignment: {
    id: string;
    status: string;
    accepted_amount_xcd: number;
    driver: {
      id: string;
      full_name: string | null;
      phone_number: string | null;
      license_plate: string | null;
      // TRUE when the winning driver is currently DDA / NISE
      // certified (server inlines the expiry check). Useful as a
      // QA signal: tourism-priority requests should mostly resolve
      // to certified drivers.
      dda_nise_certified?: boolean | null;
    };
  } | null;
}

interface CharterAdminKpis {
  open: number;
  bidding_closed: number;
  assigned: number;
  completed_today: number;
  gmv_today_xcd: number;
  gmv_30d_xcd: number;
}

interface CharterAdminApplication {
  id: string;
  driver_id: string;
  status: 'pending' | 'approved' | 'rejected' | 'withdrawn';
  submitted_at: string;
  updated_at: string;
  decided_at: string | null;
  decision_note: string | null;
  // Legacy CCL / Contract Carriage Permit columns. These are NOT
  // required under the Dominica regulatory floor — they were
  // scaffolded from Jamaica / India private-hire rules. Old rows
  // still have data; new submissions leave them null.
  charter_license_number: string | null;
  charter_license_expiry: string | null;
  contract_carriage_permit_number: string | null;
  contract_carriage_permit_expiry: string | null;
  charter_license_doc_path: string | null;
  contract_carriage_permit_doc_path: string | null;
  // DDA / NISE certification — OPTIONAL premium-tier credential.
  // When set, approving the application also flips
  // dda_nise_certified on driver_details so dispatch can prefer
  // this driver for tourism / airport / multi-stop charter
  // routing.
  dda_nise_certification_number: string | null;
  dda_nise_certification_expiry: string | null;
  dda_nise_certification_doc_path: string | null;
  service_area: string | null;
  driver_notes: string | null;
  vehicle_make: string | null;
  vehicle_model: string | null;
  vehicle_color: string | null;
  vehicle_seats: number | null;
  license_plate: string | null;
  driver: {
    id: string;
    full_name: string | null;
    phone_number: string | null;
    profile_picture_url: string | null;
  };
  driver_details: {
    verification_status: string | null;
    license_plate: string | null;
    vehicle_make: string | null;
    vehicle_model: string | null;
    vehicle_color: string | null;
    max_seats: number | null;
    charter_eligible: boolean;
    dda_nise_certified?: boolean;
    dda_nise_certification_expires_at?: string | null;
  } | null;
  kpis: { pending_count: number; completed_charters: number };
}

interface CharterAdminDriver {
  id: string;
  full_name: string | null;
  phone_number: string | null;
  profile_picture_url: string | null;
  license_plate: string | null;
  vehicle_make: string | null;
  vehicle_model: string | null;
  vehicle_color: string | null;
  max_seats: number | null;
  verification_status: string | null;
  charter_eligible: boolean;
  charter_eligible_at: string | null;
  charter_eligibility_note: string | null;
  /**
   * Set when an admin revoked this driver's charter eligibility,
   * cleared on re-approval. Drivers with this set AND
   * charter_eligible = false are surfaced under the new "Revoked"
   * filter chip with a Restore action.
   */
  charter_revoked_at: string | null;
  charter_mode: 'off' | 'alongside' | 'charter_only';
  charter_disabled_until: string | null;
  charter_no_show_count_recent: number;
  lifetime_bids: number;
  lifetime_assignments: number;
  completed_charters: number;
  /**
   * Active suspension block — `null` when the driver is in good
   * standing. `auto_three_strikes` is the automatic 14-day pause
   * triggered by 3 strikes in 60 days; `admin` is an operator-issued
   * pause of arbitrary length.
   */
  active_suspension: {
    id: string;
    source: 'auto_three_strikes' | 'admin';
    starts_at: string;
    ends_at: string;
    reason: string | null;
    created_by_admin?: string | null;
  } | null;
  /**
   * DDA / NISE Tourism Certification — Dominica's premium-tier
   * credential. When TRUE this driver is preferentially routed
   * tourism / airport / multi-stop charter requests for the first
   * 60 seconds. Server inlines the expiry check, so an expired
   * cert auto-flips to FALSE on the dispatch path even if the
   * daily sweep hasn't fired yet.
   */
  dda_nise_certified?: boolean | null;
  dda_nise_certification_expires_at?: string | null;
}

const STATUS_FILTERS = [
  { key: null as string | null, label: 'All' },
  { key: 'open', label: 'Bidding open' },
  { key: 'bidding_closed', label: 'Awaiting pick' },
  { key: 'assigned', label: 'Assigned' },
  { key: 'completed', label: 'Completed' },
  { key: 'canceled_by_passenger', label: 'Canceled' },
  { key: 'canceled_no_bids', label: 'No bids' },
  { key: 'expired', label: 'Expired' },
] as const;

const STATUS_TONE: Record<string, string> = {
  open: 'bg-emerald-500/10 text-emerald-300 border-emerald-500/30',
  bidding_closed: 'bg-amber-500/10 text-amber-300 border-amber-500/30',
  assigned: 'bg-blue-500/10 text-blue-300 border-blue-500/30',
  completed: 'bg-gray-500/10 text-gray-300 border-gray-500/30',
  canceled_by_passenger: 'bg-rose-500/10 text-rose-300 border-rose-500/30',
  canceled_no_bids: 'bg-rose-500/10 text-rose-300 border-rose-500/30',
  expired: 'bg-rose-500/10 text-rose-300 border-rose-500/30',
};

export default function Charter() {
  const [tab, setTab] = useState<'requests' | 'applications' | 'drivers'>('applications');
  const [requests, setRequests] = useState<CharterAdminRequest[]>([]);
  const [kpis, setKpis] = useState<CharterAdminKpis | null>(null);
  const [statusFilter, setStatusFilter] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  // Applications tab state
  const [applications, setApplications] = useState<CharterAdminApplication[]>([]);
  const [applicationsLoading, setApplicationsLoading] = useState(false);
  const [applicationFilter, setApplicationFilter] =
    useState<'pending' | 'approved' | 'rejected' | 'withdrawn' | 'all'>('pending');
  const [applicationQuery, setApplicationQuery] = useState('');
  const [applicationDeciding, setApplicationDeciding] = useState<string | null>(null);

  // Drivers tab state (already-approved drivers, secondary view)
  const [drivers, setDrivers] = useState<CharterAdminDriver[]>([]);
  const [driversLoading, setDriversLoading] = useState(false);
  const [driverFilter, setDriverFilter] =
    useState<DriverFilterKey>('all');
  const [driverQuery, setDriverQuery] = useState('');
  const [driverUpdating, setDriverUpdating] = useState<string | null>(null);
  // Single piece of state drives both Pause and Revoke dialogs — they
  // share identical UI (commitments preview + reason field + confirm)
  // and the action label is the only meaningful difference.
  const [destructive, setDestructive] = useState<DestructiveDialogState | null>(null);

  const refreshApplications = useCallback(async () => {
    setApplicationsLoading(true);
    try {
      const { data, error } = await supabase.rpc('admin_list_charter_applications', {
        p_status_filter: null,
      });
      if (error) throw error;
      const out = data as any;
      if (out?.ok === false) throw new Error(out.error || 'rpc failed');
      setApplications(out?.applications ?? []);
    } catch (err: any) {
      console.error('[admin-charter] applications refresh failed', err);
      toast.error(`Applications feed offline: ${err?.message ?? 'unknown'}`);
    } finally {
      setApplicationsLoading(false);
    }
  }, []);

  // Load applications on first mount + Realtime — pending counts need
  // to keep the tab badge fresh even while the admin is on another tab.
  useEffect(() => {
    void refreshApplications();
    const channel = supabase
      .channel(`admin-charter-applications-${Math.random().toString(36).slice(2)}`)
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'charter_applications' },
        () => void refreshApplications(),
      )
      .subscribe();
    return () => {
      void supabase.removeChannel(channel);
    };
  }, [refreshApplications]);

  const handleDecideApplication = useCallback(
    async (app: CharterAdminApplication, approve: boolean) => {
      const note = approve
        ? window.prompt(
            `Approve charter application from ${app.driver.full_name ?? 'this driver'}?\n\n` +
              'Optional admin note (license number reviewed, expiry, etc.). Leave blank to skip:',
            '',
          )
        : window.prompt(
            `Reject charter application from ${app.driver.full_name ?? 'this driver'}?\n\n` +
              'Rejection reason (REQUIRED, min 5 chars). The driver will see this:',
            '',
          );
      // null = canceled
      if (note === null) return;
      if (!approve && note.trim().length < 5) {
        toast.error('Rejection reason must be at least 5 characters.');
        return;
      }
      setApplicationDeciding(app.id);
      try {
        const { data, error } = await supabase.rpc('admin_decide_charter_application', {
          p_application_id: app.id,
          p_approve: approve,
          p_note: note.trim() || null,
        });
        if (error) throw error;
        if ((data as any)?.ok === false) {
          throw new Error((data as any).message || (data as any).error);
        }
        toast.success(approve ? 'Application approved.' : 'Application rejected.');
        await Promise.all([refreshApplications(), refreshDrivers()]);
      } catch (err: any) {
        toast.error(`Could not decide: ${err?.message ?? 'unknown'}`);
      } finally {
        setApplicationDeciding(null);
      }
    },
    // refreshDrivers is hoisted below — useCallback dep below pulls the
    // identifier, so we reference it through the function scope.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [refreshApplications],
  );

  const filteredApplications = useMemo(() => {
    const q = applicationQuery.trim().toLowerCase();
    return applications.filter((a) => {
      if (applicationFilter !== 'all' && a.status !== applicationFilter) return false;
      if (!q) return true;
      const hay = `${a.driver.full_name ?? ''} ${a.driver.phone_number ?? ''} ${a.license_plate ?? ''} ${a.charter_license_number ?? ''}`.toLowerCase();
      return hay.includes(q);
    });
  }, [applications, applicationFilter, applicationQuery]);

  const applicationCounts = useMemo(() => {
    return {
      pending: applications.filter((a) => a.status === 'pending').length,
      approved: applications.filter((a) => a.status === 'approved').length,
      rejected: applications.filter((a) => a.status === 'rejected').length,
      withdrawn: applications.filter((a) => a.status === 'withdrawn').length,
      total: applications.length,
    };
  }, [applications]);

  const refreshDrivers = useCallback(async () => {
    setDriversLoading(true);
    try {
      const { data, error } = await supabase.rpc('admin_list_charter_drivers');
      if (error) throw error;
      const out = data as any;
      if (out?.ok === false) throw new Error(out.error || 'rpc failed');
      setDrivers(out?.drivers ?? []);
    } catch (err: any) {
      console.error('[admin-charter] drivers refresh failed', err);
      toast.error(`Drivers feed offline: ${err?.message ?? 'unknown'}`);
    } finally {
      setDriversLoading(false);
    }
  }, []);

  // Load drivers the first time the tab opens, and whenever any
  // driver_details row changes (so freshly-approved licenses surface
  // here without manual refresh).
  useEffect(() => {
    if (tab !== 'drivers') return;
    void refreshDrivers();
    const channel = supabase
      .channel(`admin-charter-drivers-${Math.random().toString(36).slice(2)}`)
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'driver_details' },
        () => void refreshDrivers(),
      )
      .subscribe();
    return () => {
      void supabase.removeChannel(channel);
    };
  }, [tab, refreshDrivers]);

  // ── Suspension controls ────────────────────────────────────────────
  // Two distinct admin actions on a charter-approved driver:
  //
  //   • Pause (admin-issued suspension) — for arbitrary durations,
  //     1 to 365 days. Independent of the auto 3-strike pause; an
  //     admin pause can be longer or shorter.
  //
  //   • Lift — clears whichever active suspension exists (auto or
  //     admin), restores `charter_disabled_until` to NULL, and
  //     stamps the audit log so we can always trace who did it.
  //
  // These wrap `admin_suspend_charter_driver` /
  // `admin_unsuspend_charter_driver` (server RPCs in
  // 20260621060000_charter_strikes_and_suspensions). Both refresh
  // the drivers list on success so badges update without a page
  // reload.
  // Open the destructive-action dialog. The dialog itself fires the
  // commitments preview RPC + the actual destructive RPC — keeping
  // those two networking concerns inside the modal lets us cancel
  // the in-flight preview when the admin closes the modal mid-flight.
  const openDestructiveDialog = useCallback(
    (driver: CharterAdminDriver, action: DestructiveAction) => {
      setDestructive({
        driverId: driver.id,
        driverName: driver.full_name ?? 'this driver',
        action,
        commitments: null,
        loadingPreview: true,
        previewError: null,
        reason: '',
        days: 14,
        busy: false,
      });
    },
    [],
  );

  const handlePauseDriver = useCallback(
    (driver: CharterAdminDriver) => openDestructiveDialog(driver, 'pause'),
    [openDestructiveDialog],
  );

  const handleLiftSuspension = useCallback(
    async (driver: CharterAdminDriver) => {
      const reason = window.prompt(
        `Lift the charter suspension on ${driver.full_name ?? 'this driver'}?\n\n` +
          'Optional note explaining why (review complete, false positive, etc.):',
        '',
      );
      if (reason === null) return;
      setDriverUpdating(driver.id);
      try {
        const { data, error } = await supabase.rpc(
          'admin_unsuspend_charter_driver',
          { p_driver_id: driver.id, p_reason: reason.trim() || null },
        );
        if (error) throw error;
        const r = (data as any) ?? {};
        if (r.ok === false) throw new Error(r.error || 'rpc failed');
        toast.success(
          r.already_clear
            ? 'No active suspension to lift. The driver is already in good standing.'
            : 'Charter dispatch resumed.',
        );
        await refreshDrivers();
      } catch (err: any) {
        console.error('[admin-charter] lift failed', err);
        toast.error(`Couldn't lift: ${err?.message ?? 'unknown error'}`);
      } finally {
        setDriverUpdating(null);
      }
    },
    [refreshDrivers],
  );

  const handleFlipEligibility = useCallback(
    async (driver: CharterAdminDriver, nextEligible: boolean) => {
      // Approve path stays the simple prompt — there's nothing to
      // clean up when handing dispatch eligibility back to a driver.
      // Two flavors of approve in the wild:
      //   • First-time approve from the Applications tab — handled
      //     elsewhere; the Drivers tab never fires this branch for
      //     a brand-new applicant.
      //   • Reinstate a previously revoked driver (charter_revoked_at
      //     is non-null). Different copy + a successful call sends
      //     a "Charter access restored" push to the driver via
      //     send_charter_eligibility_restored_push (server-side).
      if (nextEligible) {
        const isRestoringRevoked =
          !!driver.charter_revoked_at && !driver.charter_eligible;
        const promptMsg = isRestoringRevoked
          ? `Restore charter access for ${driver.full_name ?? 'this driver'}?\n\n` +
            'Their previous reason for revocation will be cleared. They will receive a push notification telling them charter is active again.\n\n' +
            'Optional note (e.g. "license renewed Mar 5"). Leave blank to skip:'
          : `Approve ${driver.full_name ?? 'this driver'} for charter dispatch?\n\n` +
            'Optional note (license number reviewed, expiry date, etc.). Leave blank to skip:';
        const note = window.prompt(
          promptMsg,
          isRestoringRevoked ? '' : (driver.charter_eligibility_note ?? ''),
        );
        if (note === null) return;
        setDriverUpdating(driver.id);
        try {
          const { data, error } = await supabase.rpc('admin_set_charter_eligible', {
            p_driver_id: driver.id,
            p_eligible: true,
            p_note: note.trim() || null,
          });
          if (error) throw error;
          if ((data as any)?.ok === false) throw new Error((data as any).error);
          const restored = (data as any)?.restored === true;
          toast.success(
            restored
              ? 'Charter access restored. Driver was notified.'
              : 'Charter dispatch approved.',
          );
          await refreshDrivers();
        } catch (err: any) {
          toast.error(`Could not update: ${err?.message ?? 'unknown'}`);
        } finally {
          setDriverUpdating(null);
        }
        return;
      }

      // Revoke path goes through the destructive dialog — it needs
      // commitments preview + a forced-cancel confirmation step
      // when the driver has scheduled trips on the books.
      openDestructiveDialog(driver, 'revoke');
    },
    [openDestructiveDialog, refreshDrivers],
  );

  // Fired when the destructive dialog's "Confirm" button is clicked.
  // Handles both Pause and Revoke. Three-step sequence:
  //
  //   1. First call without p_force. If the server reports
  //      `live_charter_in_progress` the dialog shows a hard block.
  //      If it reports `has_scheduled_charters` the dialog flips
  //      into a confirm-cascade state (the admin has already seen
  //      the preview, so this is just "yes I really mean it").
  //   2. Once the admin confirms cascade, re-call with p_force=true.
  //   3. On success: toast counts (canceled / retracted) so the
  //      admin sees what happened, then refresh + close.
  //
  // The state machine for stages 1↔2 lives in the dialog component
  // below — see DestructiveDialog.
  const submitDestructive = useCallback(
    async (state: DestructiveDialogState, force: boolean): Promise<{
      ok: boolean;
      error?: string;
      message?: string;
      live_count?: number;
      scheduled_count?: number;
      canceled_assignments?: number;
      retracted_bids?: number;
    }> => {
      const rpcName =
        state.action === 'pause'
          ? 'admin_suspend_charter_driver'
          : 'admin_set_charter_eligible';

      const args =
        state.action === 'pause'
          ? {
              p_driver_id: state.driverId,
              p_days: state.days,
              p_reason: state.reason.trim(),
              p_force: force,
            }
          : {
              p_driver_id: state.driverId,
              p_eligible: false,
              p_note: state.reason.trim() || null,
              p_force: force,
            };

      const { data, error } = await supabase.rpc(rpcName, args);
      if (error) {
        return { ok: false, error: error.message };
      }
      const r = (data as any) ?? {};
      return {
        ok: r.ok === true,
        error: r.error,
        message: r.message,
        live_count: r.live_count,
        scheduled_count: r.scheduled_count,
        canceled_assignments: r.canceled_assignments,
        retracted_bids: r.retracted_bids,
      };
    },
    [],
  );

  // Load the commitments preview when the dialog opens. Pulls the
  // snapshot once; the admin closes + reopens to refetch (an in-
  // flight trip that completes between fetch and submit will simply
  // pass the server-side gate, so a stale-preview is safe).
  useEffect(() => {
    if (!destructive || !destructive.loadingPreview) return;
    let cancelled = false;
    (async () => {
      try {
        const { data, error } = await supabase.rpc(
          'admin_get_charter_driver_commitments',
          { p_driver_id: destructive.driverId },
        );
        if (cancelled) return;
        if (error) throw error;
        const r = data as CharterDriverCommitments | { ok: false; error: string };
        if ((r as any).ok === false) {
          throw new Error((r as any).error || 'preview failed');
        }
        setDestructive((cur) =>
          cur && cur.driverId === destructive.driverId
            ? {
                ...cur,
                commitments: r as CharterDriverCommitments,
                loadingPreview: false,
              }
            : cur,
        );
      } catch (err: any) {
        if (cancelled) return;
        setDestructive((cur) =>
          cur && cur.driverId === destructive.driverId
            ? {
                ...cur,
                loadingPreview: false,
                previewError: err?.message ?? 'Preview failed',
              }
            : cur,
        );
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [destructive]);

  const filteredDrivers = useMemo(() => {
    const q = driverQuery.trim().toLowerCase();
    const now = Date.now();
    return drivers.filter((d) => {
      const suspended =
        !!d.charter_disabled_until &&
        new Date(d.charter_disabled_until).getTime() > now;
      const revoked = !d.charter_eligible && !!d.charter_revoked_at;
      // Default ('all') view used to silently include revoked drivers
      // once the RPC started returning them, which would clutter the
      // primary "approved drivers" surface. Hide revoked rows from
      // every filter EXCEPT the dedicated 'revoked' chip — admins who
      // want to find or reinstate a revoked driver pick that chip.
      if (driverFilter !== 'revoked' && revoked) return false;
      if (driverFilter === 'active' && (d.charter_mode === 'off' || suspended)) return false;
      if (driverFilter === 'suspended' && !suspended) return false;
      if (driverFilter === 'revoked' && !revoked) return false;
      // Tourism-certified filter. Server already collapses
      // expiry into the dda_nise_certified flag, so this is a
      // one-bool check. Excludes suspended drivers — staffing
      // for tourism means picking from currently-deployable
      // certified drivers, not those in the penalty box.
      if (
        driverFilter === 'certified' &&
        (!d.dda_nise_certified || suspended)
      ) {
        return false;
      }
      if (!q) return true;
      const hay = `${d.full_name ?? ''} ${d.license_plate ?? ''} ${d.phone_number ?? ''}`.toLowerCase();
      return hay.includes(q);
    });
  }, [drivers, driverFilter, driverQuery]);

  const driverCounts = useMemo(() => {
    const now = Date.now();
    return {
      // `total` and the original chip counters describe the
      // approved-drivers population. Revoked rows are tracked
      // separately so the "All approved" chip stays an accurate
      // headcount.
      total: drivers.filter((d) => d.charter_eligible).length,
      active: drivers.filter(
        (d) => d.charter_eligible && d.charter_mode !== 'off',
      ).length,
      // Currently-deployable DDA/NISE certified drivers (cert
      // valid + charter-eligible + not suspended). Matches the
      // filter chip's tourism-staffing intent.
      certified: drivers.filter((d) => {
        if (!d.charter_eligible) return false;
        if (!d.dda_nise_certified) return false;
        const suspended =
          !!d.charter_disabled_until &&
          new Date(d.charter_disabled_until).getTime() > now;
        return !suspended;
      }).length,
      suspended: drivers.filter(
        (d) =>
          d.charter_eligible &&
          !!d.charter_disabled_until &&
          new Date(d.charter_disabled_until).getTime() > now,
      ).length,
      revoked: drivers.filter(
        (d) => !d.charter_eligible && !!d.charter_revoked_at,
      ).length,
    };
  }, [drivers]);

  const refresh = useCallback(async () => {
    try {
      const { data, error } = await supabase.rpc('admin_list_charter_requests', {
        p_status_filter: statusFilter,
        p_lookback_days: 30,
      });
      if (error) throw error;
      const out = data as any;
      if (out?.ok === false) throw new Error(out.error || 'rpc failed');
      setRequests(out?.requests ?? []);
      setKpis(out?.kpis ?? null);
    } catch (err: any) {
      console.error('[admin-charter] refresh failed', err);
      toast.error(`Charter feed offline: ${err?.message ?? 'unknown'}`, {
        duration: 6000,
      });
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [statusFilter]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  // Realtime — refresh whenever any charter table changes.
  useEffect(() => {
    const channel = supabase
      .channel(`admin-charter-${Math.random().toString(36).slice(2)}`)
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'charter_requests' },
        () => refresh(),
      )
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'charter_bids' },
        () => refresh(),
      )
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'charter_assignments' },
        () => refresh(),
      )
      .subscribe();
    return () => {
      void supabase.removeChannel(channel);
    };
  }, [refresh]);

  const handleForceCancel = useCallback(
    async (id: string, label: string) => {
      const reason = window.prompt(
        `Force-cancel charter "${label}". Enter a reason (min 5 chars):`,
        '',
      );
      if (!reason || reason.trim().length < 5) {
        if (reason !== null) toast.error('Cancellation reason must be at least 5 characters.');
        return;
      }
      try {
        const { data, error } = await supabase.rpc('admin_force_cancel_charter', {
          p_request_id: id,
          p_reason: reason.trim(),
        });
        if (error) throw error;
        if ((data as any)?.ok === false) throw new Error((data as any).error);
        toast.success('Charter canceled.');
        await refresh();
      } catch (err: any) {
        toast.error(`Could not cancel: ${err?.message ?? 'unknown'}`);
      }
    },
    [refresh],
  );

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-white flex items-center gap-2">
            <Bus className="w-6 h-6 text-emerald-400" />
            Charter Marketplace
          </h1>
          <p className="text-sm text-gray-400 mt-1">
            Live private-charter bidding feed. Sealed-bid auction with cash on
            completion (v1).
          </p>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={() => {
            if (tab === 'requests') {
              setRefreshing(true);
              refresh();
            } else if (tab === 'applications') {
              void refreshApplications();
            } else {
              void refreshDrivers();
            }
          }}
          disabled={refreshing || driversLoading || applicationsLoading}
          className="border-gray-700 text-gray-300 hover:bg-gray-800"
        >
          <RefreshCw
            className={`w-3.5 h-3.5 mr-1.5 ${refreshing || driversLoading || applicationsLoading ? 'animate-spin' : ''}`}
          />
          Refresh
        </Button>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 border-b border-gray-800">
        <TabButton
          active={tab === 'applications'}
          onClick={() => setTab('applications')}
          icon={<FileText className="w-3.5 h-3.5" />}
          label="Applications"
          count={applicationCounts.total || null}
          badge={applicationCounts.pending > 0 ? applicationCounts.pending : null}
          badgeTone="amber"
        />
        <TabButton
          active={tab === 'requests'}
          onClick={() => setTab('requests')}
          icon={<Bus className="w-3.5 h-3.5" />}
          label="Requests"
          count={kpis ? kpis.open + kpis.bidding_closed + kpis.assigned : null}
        />
        <TabButton
          active={tab === 'drivers'}
          onClick={() => setTab('drivers')}
          icon={<UserCheck className="w-3.5 h-3.5" />}
          label="Drivers"
          count={drivers.length || null}
        />
      </div>

      {tab === 'applications' ? (
        <ApplicationsPanel
          applications={filteredApplications}
          counts={applicationCounts}
          query={applicationQuery}
          onQueryChange={setApplicationQuery}
          filter={applicationFilter}
          onFilterChange={setApplicationFilter}
          loading={applicationsLoading}
          decidingId={applicationDeciding}
          onDecide={handleDecideApplication}
        />
      ) : tab === 'drivers' ? (
        <DriversPanel
          drivers={filteredDrivers}
          counts={driverCounts}
          query={driverQuery}
          onQueryChange={setDriverQuery}
          filter={driverFilter}
          onFilterChange={setDriverFilter}
          loading={driversLoading}
          updatingId={driverUpdating}
          onFlip={handleFlipEligibility}
          onPause={handlePauseDriver}
          onLift={handleLiftSuspension}
        />
      ) : (
        <RequestsPanel
          loading={loading}
          requests={requests}
          kpis={kpis}
          statusFilter={statusFilter}
          onStatusFilter={setStatusFilter}
          onForceCancel={handleForceCancel}
        />
      )}

      <DestructiveActionDialog
        state={destructive}
        onClose={() => setDestructive(null)}
        onSubmit={submitDestructive}
        onSuccess={async (msg) => {
          toast.success(msg);
          setDestructive(null);
          await refreshDrivers();
        }}
        onUpdateState={(updater) => setDestructive((cur) => (cur ? updater(cur) : cur))}
      />
    </div>
  );
}

function RequestsPanel({
  loading,
  requests,
  kpis,
  statusFilter,
  onStatusFilter,
  onForceCancel,
}: {
  loading: boolean;
  requests: CharterAdminRequest[];
  kpis: CharterAdminKpis | null;
  statusFilter: string | null;
  onStatusFilter: (k: string | null) => void;
  onForceCancel: (id: string, label: string) => void;
}) {
  return (
    <div className="space-y-6">
      {/* KPI strip */}
      <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
        <KpiCard label="Open" value={kpis?.open ?? 0} tone="emerald" pulse={(kpis?.open ?? 0) > 0} />
        <KpiCard label="Awaiting pick" value={kpis?.bidding_closed ?? 0} tone="amber" />
        <KpiCard label="Assigned" value={kpis?.assigned ?? 0} tone="blue" />
        <KpiCard label="Completed (24h)" value={kpis?.completed_today ?? 0} tone="gray" />
        <KpiCard
          label="GMV today"
          value={`EC$${Math.round(kpis?.gmv_today_xcd ?? 0).toLocaleString()}`}
          tone="emerald"
        />
        <KpiCard
          label="GMV 30d"
          value={`EC$${Math.round(kpis?.gmv_30d_xcd ?? 0).toLocaleString()}`}
          tone="emerald"
        />
      </div>

      {/* Status filter chips */}
      <div className="flex flex-wrap gap-2">
        {STATUS_FILTERS.map((s) => {
          const active = statusFilter === s.key;
          return (
            <button
              key={s.label}
              onClick={() => onStatusFilter(s.key)}
              className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all border ${
                active
                  ? 'bg-emerald-500/20 text-emerald-300 border-emerald-500/40'
                  : 'bg-gray-800/40 text-gray-400 border-gray-700/50 hover:bg-gray-700/40 hover:text-gray-200'
              }`}
            >
              {s.label}
            </button>
          );
        })}
      </div>

      {/* Request list */}
      <div className="space-y-3">
        {loading && requests.length === 0 ? (
          <div className="text-center text-sm text-gray-500 py-12">Loading charters…</div>
        ) : requests.length === 0 ? (
          <div className="text-center py-12">
            <Bus className="w-10 h-10 text-gray-600 mx-auto mb-3" />
            <h3 className="text-white font-semibold text-lg mb-1">No charters in this view.</h3>
            <p className="text-sm text-gray-400">
              {statusFilter
                ? 'Try a different status filter, or check back as drivers / passengers post requests.'
                : 'Once passengers start posting charter requests, they will appear here in real time.'}
            </p>
          </div>
        ) : (
          requests.map((r) => (
            <CharterRow
              key={r.id}
              r={r}
              onForceCancel={() =>
                onForceCancel(
                  r.id,
                  `${r.pickup_label} → ${r.dropoff_label}`,
                )
              }
            />
          ))
        )}
      </div>
    </div>
  );
}

function KpiCard({
  label,
  value,
  tone,
  pulse,
}: {
  label: string;
  value: string | number;
  tone: 'emerald' | 'amber' | 'blue' | 'gray' | 'rose';
  pulse?: boolean;
}) {
  const toneClass = {
    emerald: 'text-emerald-300',
    amber: 'text-amber-300',
    blue: 'text-blue-300',
    gray: 'text-gray-300',
    rose: 'text-rose-300',
  }[tone];
  return (
    <div className="bg-[#0e1726] border border-gray-800 rounded-xl p-3">
      <div className="flex items-center gap-1.5 mb-1">
        {pulse && (
          <span className="relative flex h-2 w-2">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-70" />
            <span className="relative inline-flex h-2 w-2 rounded-full bg-emerald-400" />
          </span>
        )}
        <p className="text-[10px] uppercase tracking-wider text-gray-500 font-semibold">
          {label}
        </p>
      </div>
      <p className={`text-xl font-bold ${toneClass}`}>{value}</p>
    </div>
  );
}

function CharterRow({
  r,
  onForceCancel,
}: {
  r: CharterAdminRequest;
  onForceCancel: () => void;
}) {
  const tone = STATUS_TONE[r.status] ?? STATUS_TONE.open;
  const closesInMs = new Date(r.bids_close_at).getTime() - Date.now();
  const closesInMin = Math.max(0, Math.ceil(closesInMs / 60000));
  const isTerminal = ['completed', 'canceled_by_passenger', 'canceled_no_bids', 'expired'].includes(
    r.status,
  );

  return (
    <div className="bg-[#0e1726] border border-gray-800 rounded-xl p-4">
      <div className="flex items-start justify-between gap-4 mb-3">
        <div className="flex items-center gap-2 flex-wrap">
          <Badge className={`${tone} border text-[10px] font-semibold uppercase tracking-wider`}>
            {r.status.replace(/_/g, ' ')}
          </Badge>
          {r.status === 'open' && (
            <span className="flex items-center gap-1 text-[11px] text-gray-400">
              <Clock className="w-3 h-3" />
              {closesInMin <= 0 ? 'closing…' : `closes in ${closesInMin}m`}
            </span>
          )}
          <span className="flex items-center gap-1 text-[11px] text-gray-400">
            <Users className="w-3 h-3" />
            {r.passenger_count} pax
            {r.vehicle_class !== 'any' ? ` · ${r.vehicle_class}` : ''}
          </span>
          {r.budget_ceiling_xcd != null && (
            <span className="flex items-center gap-1 text-[11px] text-gray-400">
              <Banknote className="w-3 h-3" />
              budget hint EC${r.budget_ceiling_xcd}
            </span>
          )}
          {/*
            Tourism-priority badge. Surfaces requests where the
            passenger opted into 60-second DDA / NISE certified-only
            window. Useful for ops to see which trips were premium-
            routed and which assignments paired with certified
            drivers (audit / dispute review).
          */}
          {r.prefer_tourism_certified && (
            <Badge className="bg-emerald-500/15 text-emerald-300 border-emerald-500/40 border text-[10px] font-semibold uppercase tracking-wider">
              ★ Tourism priority
            </Badge>
          )}
        </div>
        {!isTerminal && r.status !== 'completed' && (
          <Button
            variant="outline"
            size="sm"
            onClick={onForceCancel}
            className="h-7 px-2 text-xs border-rose-700/50 text-rose-300 hover:bg-rose-900/30 hover:text-rose-200"
            title="Force-cancel this charter (admin only)"
          >
            <Trash2 className="w-3 h-3 mr-1" />
            Cancel
          </Button>
        )}
      </div>

      <div className="flex items-center gap-2 text-sm text-white">
        <MapPin className="w-3.5 h-3.5 text-emerald-400 shrink-0" />
        <span className="truncate">{r.pickup_label}</span>
        <span className="text-gray-600">→</span>
        <MapPin className="w-3.5 h-3.5 text-rose-400 shrink-0" />
        <span className="truncate">{r.dropoff_label}</span>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mt-3 text-xs">
        {/* Passenger */}
        <div className="bg-gray-800/40 rounded-lg p-2.5">
          <p className="text-[10px] uppercase tracking-wider text-gray-500 mb-1">
            Passenger
          </p>
          <p className="text-gray-200 font-medium truncate">
            {r.passenger.full_name || 'Unknown'}
          </p>
          {r.passenger.phone_number && (
            <p className="text-gray-500 font-mono text-[11px]">{r.passenger.phone_number}</p>
          )}
        </div>

        {/* Bidding */}
        <div className="bg-gray-800/40 rounded-lg p-2.5">
          <p className="text-[10px] uppercase tracking-wider text-gray-500 mb-1">
            Bids
          </p>
          <p className="text-gray-200 font-medium">
            {r.live_bid_count} live · {r.total_bid_count} total
          </p>
          {r.lowest_bid_xcd != null && (
            <p className="text-gray-500 text-[11px]">
              Lowest: EC${Number(r.lowest_bid_xcd).toFixed(2)}
            </p>
          )}
        </div>

        {/* Driver */}
        <div className="bg-gray-800/40 rounded-lg p-2.5">
          <p className="text-[10px] uppercase tracking-wider text-gray-500 mb-1">
            Driver
          </p>
          {r.assignment ? (
            <>
              <div className="flex items-center gap-1.5">
                <p className="text-gray-200 font-medium truncate">
                  {r.assignment.driver.full_name || 'Driver'}
                </p>
                {r.assignment.driver.dda_nise_certified && (
                  <span
                    className="text-emerald-300 text-[9px] font-bold leading-none border border-emerald-500/40 bg-emerald-500/10 rounded px-1 py-0.5 tracking-wider"
                    title="DDA / NISE Tourism Certified"
                  >
                    ★ NISE
                  </span>
                )}
              </div>
              <p className="text-gray-500 text-[11px]">
                {r.assignment.driver.license_plate || '–'} · EC$
                {Number(r.assignment.accepted_amount_xcd).toFixed(2)} ·{' '}
                {r.assignment.status.replace(/_/g, ' ')}
              </p>
            </>
          ) : (
            <p className="text-gray-500 italic">unassigned</p>
          )}
        </div>
      </div>

      {/* Schedule line */}
      <div className="flex items-center gap-3 mt-3 text-[11px] text-gray-500">
        <span>
          Posted {new Date(r.created_at).toLocaleString(undefined, { dateStyle: 'short', timeStyle: 'short' })}
        </span>
        <span>·</span>
        <span>
          Pickup{' '}
          {new Date(r.requested_pickup_at).toLocaleString(undefined, {
            dateStyle: 'short',
            timeStyle: 'short',
          })}
        </span>
        {r.cancel_reason && (
          <>
            <span>·</span>
            <span className="text-rose-400 flex items-center gap-1">
              <AlertTriangle className="w-3 h-3" /> {r.cancel_reason}
            </span>
          </>
        )}
      </div>
    </div>
  );
}

// ─── Tab button ─────────────────────────────────────────────────────────────

function TabButton({
  active,
  onClick,
  icon,
  label,
  count,
  badge,
  badgeTone,
}: {
  active: boolean;
  onClick: () => void;
  icon: React.ReactNode;
  label: string;
  count?: number | null;
  badge?: number | null;
  badgeTone?: 'amber' | 'emerald';
}) {
  const badgeClass =
    badgeTone === 'amber'
      ? 'bg-amber-500/20 text-amber-300 border-amber-500/40'
      : 'bg-emerald-500/20 text-emerald-300 border-emerald-500/40';
  return (
    <button
      onClick={onClick}
      className={`relative px-4 py-2.5 -mb-px flex items-center gap-2 text-sm font-medium border-b-2 transition-colors ${
        active
          ? 'border-emerald-400 text-emerald-300'
          : 'border-transparent text-gray-400 hover:text-gray-200'
      }`}
    >
      {icon}
      <span>{label}</span>
      {count != null && (
        <span className="text-[11px] text-gray-500 font-mono">({count})</span>
      )}
      {badge != null && badge > 0 && (
        <span className={`ml-1 px-1.5 py-0.5 rounded-full border text-[10px] font-semibold ${badgeClass}`}>
          {badge}
        </span>
      )}
    </button>
  );
}

// ─── Drivers panel ─────────────────────────────────────────────────────────

type DriverFilterKey =
  | 'all'
  | 'active'
  | 'certified'
  | 'suspended'
  | 'revoked';

const DRIVER_FILTER_CHIPS: { key: DriverFilterKey; label: string }[] = [
  { key: 'all', label: 'All approved' },
  { key: 'active', label: 'Currently online' },
  // Tourism-priority slice. Hides anyone whose DDA / NISE cert has
  // lapsed or who isn't certified, regardless of their charter
  // mode. Useful when an operator is staffing for a known tourism
  // surge (cruise day, airport peak, festival) and wants to see
  // who's currently eligible to take tourism-priority requests.
  { key: 'certified', label: '★ NISE certified' },
  { key: 'suspended', label: 'Suspended' },
  { key: 'revoked', label: 'Revoked' },
];

function DriversPanel({
  drivers,
  counts,
  query,
  onQueryChange,
  filter,
  onFilterChange,
  loading,
  updatingId,
  onFlip,
  onPause,
  onLift,
}: {
  drivers: CharterAdminDriver[];
  counts: {
    total: number;
    active: number;
    certified: number;
    suspended: number;
    revoked: number;
  };
  query: string;
  onQueryChange: (s: string) => void;
  filter: DriverFilterKey;
  onFilterChange: (s: DriverFilterKey) => void;
  loading: boolean;
  updatingId: string | null;
  onFlip: (driver: CharterAdminDriver, eligible: boolean) => void;
  onPause: (driver: CharterAdminDriver) => void;
  onLift: (driver: CharterAdminDriver) => void;
}) {
  return (
    <div className="space-y-5">
      {/* Helper banner — keep the Drivers/Applications mental model crisp */}
      <div className="bg-emerald-500/5 border border-emerald-500/20 rounded-xl p-3 text-xs text-emerald-200/90">
        <strong className="font-semibold">Approved charter drivers.</strong>{' '}
        New driver requests live in the <em>Applications</em> tab. This view
        is for managing drivers who are already cleared for charter dispatch.
      </div>

      {/* Driver KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <KpiCard label="Approved" value={counts.total} tone="emerald" />
        <KpiCard label="Currently online" value={counts.active} tone="blue" />
        <KpiCard
          label="★ NISE certified"
          value={counts.certified}
          tone="emerald"
        />
        <KpiCard
          label="Suspended"
          value={counts.suspended}
          tone="gray"
          pulse={counts.suspended > 0}
        />
        <KpiCard
          label="Revoked"
          value={counts.revoked}
          tone="rose"
          pulse={counts.revoked > 0}
        />
      </div>

      {/* Search + filter */}
      <div className="flex flex-col md:flex-row gap-3 md:items-center md:justify-between">
        <input
          type="search"
          value={query}
          onChange={(e) => onQueryChange(e.target.value)}
          placeholder="Search by name, plate, or phone…"
          className="bg-[#0e1726] border border-gray-800 rounded-lg px-3 py-2 text-sm text-white placeholder-gray-600 w-full md:w-80 focus:outline-none focus:border-emerald-500/50"
        />
        <div className="flex flex-wrap gap-2">
          {DRIVER_FILTER_CHIPS.map((c) => {
            const active = filter === c.key;
            return (
              <button
                key={c.key}
                onClick={() => onFilterChange(c.key)}
                className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-all ${
                  active
                    ? 'bg-emerald-500/20 text-emerald-300 border-emerald-500/40'
                    : 'bg-gray-800/40 text-gray-400 border-gray-700/50 hover:bg-gray-700/40 hover:text-gray-200'
                }`}
              >
                {c.label}
              </button>
            );
          })}
        </div>
      </div>

      {/* Driver list */}
      <div className="space-y-3">
        {loading && drivers.length === 0 ? (
          <div className="text-center text-sm text-gray-500 py-12">Loading drivers…</div>
        ) : drivers.length === 0 ? (
          <div className="text-center py-12">
            <UserCheck className="w-10 h-10 text-gray-600 mx-auto mb-3" />
            <h3 className="text-white font-semibold text-lg mb-1">
              No drivers in this view.
            </h3>
            <p className="text-sm text-gray-400">
              {filter === 'active'
                ? 'No approved drivers have flipped charter mode on right now.'
                : filter === 'certified'
                  ? 'No currently-deployable DDA / NISE certified drivers. Once you approve charter applications that included a valid cert, they appear here.'
                  : filter === 'suspended'
                    ? 'No drivers are currently suspended from charter dispatch.'
                    : filter === 'revoked'
                      ? 'No drivers have been revoked. Use this view to find and reinstate drivers whose charter access was previously removed.'
                      : 'No drivers have been approved for charter dispatch yet. Review the Applications tab to start the queue.'}
            </p>
          </div>
        ) : (
          drivers.map((d) => (
            <DriverRow
              key={d.id}
              d={d}
              updating={updatingId === d.id}
              onFlip={onFlip}
              onPause={onPause}
              onLift={onLift}
            />
          ))
        )}
      </div>
    </div>
  );
}

function DriverRow({
  d,
  updating,
  onFlip,
  onPause,
  onLift,
}: {
  d: CharterAdminDriver;
  updating: boolean;
  onFlip: (driver: CharterAdminDriver, eligible: boolean) => void;
  onPause: (driver: CharterAdminDriver) => void;
  onLift: (driver: CharterAdminDriver) => void;
}) {
  const vehicleLabel = [d.vehicle_color, d.vehicle_make, d.vehicle_model]
    .filter(Boolean)
    .join(' ')
    .trim();

  // Revoked drivers no longer have charter_eligible = TRUE but we
  // surface them in the Drivers tab (under the "Revoked" filter) so
  // admins can reinstate without forcing the driver to re-apply.
  // The row's whole shape changes for revoked rows: no Approved /
  // Charter-mode / Suspended badges, just a single REVOKED badge +
  // revoke metadata panel + Restore button.
  const isRevoked = !d.charter_eligible && !!d.charter_revoked_at;

  // Suspension is the source of truth for "is this driver actually
  // delivering charters right now?". Trust the active_suspension
  // block first, fall back to the legacy `charter_disabled_until`
  // column for older rows that haven't been recomputed yet.
  const isSuspended =
    !isRevoked &&
    (!!d.active_suspension ||
      (!!d.charter_disabled_until &&
        new Date(d.charter_disabled_until).getTime() > Date.now()));
  const suspensionEndsAt = d.active_suspension?.ends_at ?? d.charter_disabled_until;
  const suspensionLabel = (() => {
    if (!isSuspended) return null;
    if (d.active_suspension?.source === 'auto_three_strikes') return 'Auto-paused (3 strikes)';
    if (d.active_suspension?.source === 'admin') return 'Admin-paused';
    return 'Suspended';
  })();

  return (
    <div className="bg-[#0e1726] border border-gray-800 rounded-xl p-4">
      <div className="flex items-start gap-4">
        {/* Avatar */}
        {d.profile_picture_url ? (
          <img
            src={d.profile_picture_url}
            alt={d.full_name ?? 'Driver'}
            className="w-12 h-12 rounded-full object-cover border border-gray-700"
          />
        ) : (
          <div className="w-12 h-12 rounded-full bg-gray-800 border border-gray-700 flex items-center justify-center text-gray-500 text-sm font-semibold">
            {(d.full_name ?? '?').slice(0, 1).toUpperCase()}
          </div>
        )}

        {/* Identity + status */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <h3 className="text-white font-semibold truncate">
              {d.full_name ?? 'Unnamed driver'}
            </h3>
            {isRevoked ? (
              <Badge className="bg-rose-500/15 text-rose-300 border-rose-500/40 border text-[10px] uppercase tracking-wider font-semibold">
                <ShieldX className="w-2.5 h-2.5 mr-1" />
                Revoked
              </Badge>
            ) : (
              <>
                <Badge className="bg-emerald-500/15 text-emerald-300 border-emerald-500/30 border text-[10px] uppercase tracking-wider font-semibold">
                  <ShieldCheck className="w-2.5 h-2.5 mr-1" />
                  Approved
                </Badge>
                {d.charter_mode !== 'off' && (
                  <Badge className="bg-blue-500/15 text-blue-300 border-blue-500/30 border text-[10px] uppercase tracking-wider font-semibold">
                    {d.charter_mode === 'charter_only' ? 'Charter only' : 'Charter alongside'}
                  </Badge>
                )}
                {isSuspended && (
                  <Badge className="bg-rose-500/15 text-rose-300 border-rose-500/30 border text-[10px] uppercase tracking-wider font-semibold">
                    {suspensionLabel}
                  </Badge>
                )}
                {/*
                  DDA / NISE Tourism Certified badge. Premium tier
                  credential — drivers with it get the 60s exclusive
                  window on tourism-priority requests. We only show
                  the badge when the cert is currently valid; the
                  server's `dda_nise_certified` field already
                  collapses the expiry check, so this is a one-bool
                  decision.
                */}
                {d.dda_nise_certified && (
                  <Badge
                    className="bg-emerald-500/15 text-emerald-300 border-emerald-500/30 border text-[10px] uppercase tracking-wider font-semibold"
                    title={
                      d.dda_nise_certification_expires_at
                        ? `Expires ${new Date(d.dda_nise_certification_expires_at).toLocaleDateString()}`
                        : 'DDA / NISE Tourism Certified'
                    }
                  >
                    ★ NISE Tourism
                  </Badge>
                )}
              </>
            )}
          </div>
          <p className="text-xs text-gray-400 mt-0.5 truncate">
            {d.license_plate ?? 'No plate'}
            {vehicleLabel ? `  ·  ${vehicleLabel}` : ''}
            {d.max_seats ? `  ·  ${d.max_seats} seats` : ''}
            {d.phone_number ? `  ·  ${d.phone_number}` : ''}
          </p>

          {/* Stats */}
          <div className="flex flex-wrap gap-x-4 gap-y-1 mt-2 text-[11px] text-gray-500">
            <span>{d.lifetime_bids} lifetime bids</span>
            <span>{d.lifetime_assignments} won</span>
            <span>{d.completed_charters} completed</span>
            {d.charter_no_show_count_recent > 0 && (
              <span className="text-rose-400">
                {d.charter_no_show_count_recent}/3 strikes (60d)
              </span>
            )}
            {!isRevoked && d.charter_eligible_at && (
              <span>
                Approved {new Date(d.charter_eligible_at).toLocaleDateString()}
              </span>
            )}
            {isRevoked && d.charter_revoked_at && (
              <span className="text-rose-400">
                Revoked {new Date(d.charter_revoked_at).toLocaleDateString()}
              </span>
            )}
          </div>

          {/* Active suspension panel — lives below the stats line so
              the operator can see WHY this driver is paused without
              clicking through. The end timestamp is the auto-unpause
              deadline; an admin lift via the button on the right
              clears the row before that. */}
          {isSuspended && suspensionEndsAt && (
            <div className="mt-2 rounded-md border border-rose-500/30 bg-rose-500/5 px-3 py-2 text-[11px] text-rose-100/90">
              <div className="font-semibold text-rose-200">
                {d.active_suspension?.source === 'admin'
                  ? 'Paused by admin'
                  : 'Auto-paused: 3 strikes in 60 days'}
              </div>
              <div className="mt-0.5 text-rose-200/70">
                Auto-lifts {new Date(suspensionEndsAt).toLocaleString()}
                {d.active_suspension?.reason
                  ? ` · ${d.active_suspension.reason}`
                  : ''}
              </div>
            </div>
          )}

          {/* Revoke panel — analogous to the suspension panel above.
              Carries the original revoke reason + timestamp so the
              admin can decide whether to reinstate. The Restore
              button on the right is the inverse action. */}
          {isRevoked && (
            <div className="mt-2 rounded-md border border-rose-500/40 bg-rose-500/10 px-3 py-2 text-[11px] text-rose-100/90">
              <div className="font-semibold text-rose-200">
                Charter access revoked
              </div>
              <div className="mt-0.5 text-rose-200/80">
                {d.charter_revoked_at
                  ? `Revoked ${new Date(d.charter_revoked_at).toLocaleString()}`
                  : 'Revoked'}
                {d.charter_eligibility_note
                  ? ` · "${d.charter_eligibility_note}"`
                  : ''}
              </div>
            </div>
          )}

          {!isRevoked && d.charter_eligibility_note && (
            <p className="text-[11px] text-gray-500 mt-1.5 italic truncate">
              Note: {d.charter_eligibility_note}
            </p>
          )}
        </div>

        {/* Actions:
              • Restore — reinstates a previously revoked driver and
                pushes them a "Charter access restored" notification.
                Only renders for revoked rows.
              • Pause   — admin issues a charter-only suspension of any
                length. Eligible-and-not-paused rows.
              • Lift    — clears the active suspension early.
              • Revoke  — strips charter eligibility entirely. Separate
                from pause: revoke kicks them out of the marketplace,
                pause is a temporary penalty box. */}
        <div className="shrink-0 flex flex-col gap-1.5 items-stretch min-w-[140px]">
          {isRevoked ? (
            <Button
              variant="outline"
              size="sm"
              disabled={updating}
              onClick={() => onFlip(d, true)}
              className="h-8 px-3 text-xs border-emerald-700/50 text-emerald-300 hover:bg-emerald-900/30"
              title="Reinstate this driver's charter access and notify them"
            >
              <CheckCircle2 className="w-3 h-3 mr-1" />
              {updating ? 'Restoring…' : 'Restore access'}
            </Button>
          ) : (
            <>
              {isSuspended ? (
                <Button
                  variant="outline"
                  size="sm"
                  disabled={updating}
                  onClick={() => onLift(d)}
                  className="h-8 px-3 text-xs border-emerald-700/50 text-emerald-300 hover:bg-emerald-900/30"
                  title="Lift the active charter suspension early"
                >
                  <CheckCircle2 className="w-3 h-3 mr-1" />
                  {updating ? 'Updating…' : 'Lift pause'}
                </Button>
              ) : (
                <Button
                  variant="outline"
                  size="sm"
                  disabled={updating}
                  onClick={() => onPause(d)}
                  className="h-8 px-3 text-xs border-amber-700/50 text-amber-300 hover:bg-amber-900/30"
                  title="Pause this driver's charter dispatch"
                >
                  <AlertTriangle className="w-3 h-3 mr-1" />
                  Pause
                </Button>
              )}
              <Button
                variant="outline"
                size="sm"
                disabled={updating}
                onClick={() => onFlip(d, false)}
                className="h-8 px-3 text-xs border-rose-700/50 text-rose-300 hover:bg-rose-900/30"
                title="Strip charter eligibility entirely"
              >
                <XCircle className="w-3 h-3 mr-1" />
                Revoke
              </Button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Applications panel ────────────────────────────────────────────────────

const APPLICATION_FILTER_CHIPS: {
  key: 'pending' | 'approved' | 'rejected' | 'withdrawn' | 'all';
  label: string;
}[] = [
  { key: 'pending', label: 'Pending review' },
  { key: 'approved', label: 'Approved' },
  { key: 'rejected', label: 'Rejected' },
  { key: 'withdrawn', label: 'Withdrawn' },
  { key: 'all', label: 'All' },
];

function ApplicationsPanel({
  applications,
  counts,
  query,
  onQueryChange,
  filter,
  onFilterChange,
  loading,
  decidingId,
  onDecide,
}: {
  applications: CharterAdminApplication[];
  counts: { total: number; pending: number; approved: number; rejected: number; withdrawn: number };
  query: string;
  onQueryChange: (s: string) => void;
  filter: 'pending' | 'approved' | 'rejected' | 'withdrawn' | 'all';
  onFilterChange: (s: 'pending' | 'approved' | 'rejected' | 'withdrawn' | 'all') => void;
  loading: boolean;
  decidingId: string | null;
  onDecide: (a: CharterAdminApplication, approve: boolean) => void;
}) {
  return (
    <div className="space-y-5">
      {/* KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <KpiCard
          label="Pending review"
          value={counts.pending}
          tone="amber"
          pulse={counts.pending > 0}
        />
        <KpiCard label="Approved" value={counts.approved} tone="emerald" />
        <KpiCard label="Rejected" value={counts.rejected} tone="gray" />
        <KpiCard label="Withdrawn" value={counts.withdrawn} tone="gray" />
      </div>

      {/* Search + filter */}
      <div className="flex flex-col md:flex-row gap-3 md:items-center md:justify-between">
        <input
          type="search"
          value={query}
          onChange={(e) => onQueryChange(e.target.value)}
          placeholder="Search by name, phone, plate, or license #…"
          className="bg-[#0e1726] border border-gray-800 rounded-lg px-3 py-2 text-sm text-white placeholder-gray-600 w-full md:w-80 focus:outline-none focus:border-emerald-500/50"
        />
        <div className="flex flex-wrap gap-2">
          {APPLICATION_FILTER_CHIPS.map((c) => {
            const active = filter === c.key;
            return (
              <button
                key={c.key}
                onClick={() => onFilterChange(c.key)}
                className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-all ${
                  active
                    ? 'bg-emerald-500/20 text-emerald-300 border-emerald-500/40'
                    : 'bg-gray-800/40 text-gray-400 border-gray-700/50 hover:bg-gray-700/40 hover:text-gray-200'
                }`}
              >
                {c.label}
              </button>
            );
          })}
        </div>
      </div>

      {/* List */}
      <div className="space-y-3">
        {loading && applications.length === 0 ? (
          <div className="text-center text-sm text-gray-500 py-12">Loading applications…</div>
        ) : applications.length === 0 ? (
          <div className="text-center py-12">
            <FileText className="w-10 h-10 text-gray-600 mx-auto mb-3" />
            <h3 className="text-white font-semibold text-lg mb-1">
              No applications in this view.
            </h3>
            <p className="text-sm text-gray-400">
              {filter === 'pending'
                ? 'Drivers who apply for charter dispatch from the mobile app will appear here.'
                : 'No applications match this filter.'}
            </p>
          </div>
        ) : (
          applications.map((a) => (
            <ApplicationRow
              key={a.id}
              a={a}
              deciding={decidingId === a.id}
              onDecide={onDecide}
            />
          ))
        )}
      </div>
    </div>
  );
}

function ApplicationRow({
  a,
  deciding,
  onDecide,
}: {
  a: CharterAdminApplication;
  deciding: boolean;
  onDecide: (a: CharterAdminApplication, approve: boolean) => void;
}) {
  const vehicleLabel = [a.vehicle_color, a.vehicle_make, a.vehicle_model]
    .filter(Boolean)
    .join(' ')
    .trim();

  const statusBadge = (() => {
    switch (a.status) {
      case 'pending':
        return (
          <Badge className="bg-amber-500/15 text-amber-300 border-amber-500/30 border text-[10px] uppercase tracking-wider font-semibold">
            <Clock className="w-2.5 h-2.5 mr-1" /> Pending review
          </Badge>
        );
      case 'approved':
        return (
          <Badge className="bg-emerald-500/15 text-emerald-300 border-emerald-500/30 border text-[10px] uppercase tracking-wider font-semibold">
            <ShieldCheck className="w-2.5 h-2.5 mr-1" /> Approved
          </Badge>
        );
      case 'rejected':
        return (
          <Badge className="bg-rose-500/15 text-rose-300 border-rose-500/30 border text-[10px] uppercase tracking-wider font-semibold">
            <ShieldX className="w-2.5 h-2.5 mr-1" /> Rejected
          </Badge>
        );
      case 'withdrawn':
        return (
          <Badge className="bg-gray-500/15 text-gray-300 border-gray-500/30 border text-[10px] uppercase tracking-wider font-semibold">
            Withdrawn
          </Badge>
        );
    }
  })();

  return (
    <div className="bg-[#0e1726] border border-gray-800 rounded-xl p-4">
      <div className="flex items-start gap-4">
        {/* Avatar */}
        {a.driver.profile_picture_url ? (
          <img
            src={a.driver.profile_picture_url}
            alt={a.driver.full_name ?? 'Driver'}
            className="w-12 h-12 rounded-full object-cover border border-gray-700"
          />
        ) : (
          <div className="w-12 h-12 rounded-full bg-gray-800 border border-gray-700 flex items-center justify-center text-gray-500 text-sm font-semibold">
            {(a.driver.full_name ?? '?').slice(0, 1).toUpperCase()}
          </div>
        )}

        {/* Identity + meta */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <h3 className="text-white font-semibold truncate">
              {a.driver.full_name ?? 'Unnamed driver'}
            </h3>
            {statusBadge}
            {a.driver_details?.charter_eligible && (
              <Badge className="bg-emerald-500/15 text-emerald-300 border-emerald-500/30 border text-[10px] uppercase tracking-wider font-semibold">
                Already approved
              </Badge>
            )}
          </div>

          <p className="text-xs text-gray-400 mt-0.5 truncate">
            {a.license_plate ?? 'No plate'}
            {vehicleLabel ? `  ·  ${vehicleLabel}` : ''}
            {a.vehicle_seats ? `  ·  ${a.vehicle_seats} seats` : ''}
            {a.driver.phone_number ? `  ·  ${a.driver.phone_number}` : ''}
          </p>

          {/* ── Standard-document linkage ────────────────────────
              Surfaces the driver's underlying verification_status so
              the operator doesn't approve a charter application for
              a driver whose license / PSV / insurance / selfie
              haven't been verified through the regular Documents
              flow. The charter application form only collects
              CHARTER-specific credentials (CCL + permit) on purpose;
              the rest is supposed to already be on file. */}
          <StandardVerificationChip
            status={a.driver_details?.verification_status ?? null}
          />

          {/* DDA / NISE premium-tier panel — Dominica's only
              charter-specific credential, and it's OPTIONAL. When the
              application carries one, approving the charter app also
              flips dda_nise_certified on driver_details so dispatch
              can preferentially route tourism / airport / multi-stop
              requests to this driver. When it doesn't, the chip
              still helps the operator see at a glance whether this
              applicant has tourism certification or not. */}
          <div className="grid grid-cols-1 gap-3 mt-3 text-xs">
            <div
              className={`rounded-lg p-2.5 border ${
                a.dda_nise_certification_doc_path ||
                a.dda_nise_certification_number
                  ? 'bg-emerald-500/10 border-emerald-500/30'
                  : 'bg-gray-800/40 border-gray-700/40'
              }`}
            >
              <div className="flex items-center gap-2 mb-1">
                <p className="text-[10px] uppercase tracking-wider text-gray-400">
                  DDA / NISE certification
                </p>
                {a.dda_nise_certification_doc_path ||
                a.dda_nise_certification_number ? (
                  <Badge className="bg-emerald-500/20 text-emerald-200 border-emerald-500/40 border text-[9px] uppercase tracking-wider font-semibold">
                    Premium tier
                  </Badge>
                ) : (
                  <Badge className="bg-gray-700/40 text-gray-400 border-gray-700/60 border text-[9px] uppercase tracking-wider font-semibold">
                    Standard tier
                  </Badge>
                )}
              </div>
              <p className="text-gray-200 font-medium">
                {a.dda_nise_certification_number ?? (
                  <span className="text-gray-500 font-normal italic">
                    No DDA / NISE certification provided. Basic
                    charter approval only.
                  </span>
                )}
              </p>
              {a.dda_nise_certification_expiry && (
                <p className="text-gray-500 text-[11px] flex items-center gap-1 mt-0.5">
                  <CalendarDays className="w-3 h-3" />
                  Expires{' '}
                  {new Date(a.dda_nise_certification_expiry).toLocaleDateString()}
                </p>
              )}
              <DocLink
                path={a.dda_nise_certification_doc_path}
                label="View DDA / NISE certificate"
              />
            </div>
          </div>

          {/* Legacy CCL / CCP fallback panel — only renders if old
              data exists on the row so we don't lose audit context
              for applications submitted before the Dominica
              regulatory correction. Hidden entirely on new-style
              applications. */}
          {(a.charter_license_number ||
            a.charter_license_doc_path ||
            a.contract_carriage_permit_number ||
            a.contract_carriage_permit_doc_path) && (
            <div className="mt-3 p-2.5 rounded-lg bg-amber-500/5 border border-amber-500/20 text-xs">
              <p className="text-[10px] uppercase tracking-wider text-amber-400 mb-1 font-semibold">
                Legacy CCL / CCP data
              </p>
              <p className="text-gray-400 text-[11px] mb-2 leading-snug">
                Submitted under the previous schema. CCL / Contract
                Carriage Permit aren&apos;t required in Dominica;
                kept here only for audit history.
              </p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                {(a.charter_license_number ||
                  a.charter_license_doc_path) && (
                  <div>
                    <p className="text-gray-500 text-[11px]">
                      CCL: {a.charter_license_number ?? '–'}
                    </p>
                    <DocLink
                      path={a.charter_license_doc_path}
                      label="View CCL photo"
                    />
                  </div>
                )}
                {(a.contract_carriage_permit_number ||
                  a.contract_carriage_permit_doc_path) && (
                  <div>
                    <p className="text-gray-500 text-[11px]">
                      CCP: {a.contract_carriage_permit_number ?? '–'}
                    </p>
                    <DocLink
                      path={a.contract_carriage_permit_doc_path}
                      label="View CCP photo"
                    />
                  </div>
                )}
              </div>
            </div>
          )}

          {(a.service_area || a.driver_notes) && (
            <div className="mt-3 space-y-1.5 text-xs">
              {a.service_area && (
                <p className="text-gray-300">
                  <span className="text-gray-500 uppercase tracking-wider text-[10px] mr-2">
                    Service area
                  </span>
                  {a.service_area}
                </p>
              )}
              {a.driver_notes && (
                <p className="text-gray-300">
                  <span className="text-gray-500 uppercase tracking-wider text-[10px] mr-2">
                    Notes
                  </span>
                  {a.driver_notes}
                </p>
              )}
            </div>
          )}

          {/* Footer line */}
          <div className="flex flex-wrap gap-x-4 gap-y-1 mt-3 text-[11px] text-gray-500">
            <span>Submitted {new Date(a.submitted_at).toLocaleString(undefined, { dateStyle: 'short', timeStyle: 'short' })}</span>
            {a.decided_at && (
              <span>
                Decided {new Date(a.decided_at).toLocaleString(undefined, { dateStyle: 'short', timeStyle: 'short' })}
              </span>
            )}
            {a.kpis && (
              <span>{a.kpis.completed_charters} completed charters</span>
            )}
          </div>

          {a.decision_note && (
            <p className="text-[11px] text-gray-400 mt-2 italic">
              {a.status === 'rejected' ? 'Rejection reason: ' : 'Admin note: '}
              {a.decision_note}
            </p>
          )}
        </div>

        {/* Actions */}
        {a.status === 'pending' && (
          <div className="shrink-0 flex flex-col gap-2">
            <Button
              size="sm"
              disabled={deciding}
              onClick={() => onDecide(a, true)}
              className="h-8 px-3 text-xs bg-emerald-500/20 text-emerald-300 border border-emerald-500/40 hover:bg-emerald-500/30"
            >
              <CheckCircle2 className="w-3 h-3 mr-1" />
              {deciding ? 'Working…' : 'Approve'}
            </Button>
            <Button
              variant="outline"
              size="sm"
              disabled={deciding}
              onClick={() => onDecide(a, false)}
              className="h-8 px-3 text-xs border-rose-700/50 text-rose-300 hover:bg-rose-900/30 hover:text-rose-200"
            >
              <XCircle className="w-3 h-3 mr-1" />
              Reject
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}

// Surfaces the driver's underlying driver_documents verification
// state directly inside the charter application row. The charter app
// itself ONLY collects charter-specific credentials (CCL + permit);
// everything else (license, PSV, insurance, selfie) lives in the
// driver_documents flow and is reviewed through /admin/verification.
// This chip + warning makes the dependency unmistakable so an admin
// doesn't approve charter for a driver whose underlying papers
// aren't clean.
function StandardVerificationChip({ status }: { status: string | null }) {
  // null means we never loaded driver_details; treat as unknown.
  const s = status ?? 'unknown';

  let tone:
    | 'emerald'
    | 'amber'
    | 'rose'
    | 'gray' = 'amber';
  let icon = <Clock className="w-3 h-3 mr-1" />;
  let label = 'Standard docs pending';
  let body: string | null =
    'License, PSV, insurance, or selfie not yet approved. Resolve in Driver Verification before approving charter.';

  switch (s) {
    case 'verified':
      tone = 'emerald';
      icon = <ShieldCheck className="w-3 h-3 mr-1" />;
      label = 'Standard docs verified';
      body = null;
      break;
    case 'pending_documents':
      tone = 'amber';
      icon = <Clock className="w-3 h-3 mr-1" />;
      label = 'Quick Start: full set incomplete';
      body =
        'Floor docs (license + selfie) approved. PSV permit or insurance still missing or in review. Review the standard set before approving charter.';
      break;
    case 'pending_verification':
      tone = 'amber';
      icon = <Clock className="w-3 h-3 mr-1" />;
      label = 'Standard docs in review';
      body =
        'License or selfie still in the verification queue. Approve those first; charter approval here doesn\'t override the standard floor gate.';
      break;
    case 'rejected':
      tone = 'rose';
      icon = <ShieldX className="w-3 h-3 mr-1" />;
      label = 'Standard docs rejected';
      body =
        'One or more of the driver\'s required documents was rejected. They cannot operate at all. Fix the underlying rejection before considering charter.';
      break;
    case 'expired_document':
      tone = 'rose';
      icon = <ShieldAlert className="w-3 h-3 mr-1" />;
      label = 'Standard doc expired';
      body =
        'A required document has expired. The driver is currently blocked from going online. Resolve the expiration before approving charter.';
      break;
    default:
      tone = 'gray';
      icon = <ShieldAlert className="w-3 h-3 mr-1" />;
      label = 'Verification status unknown';
      body =
        'Could not read this driver\'s standard verification state. Open Driver Verification to investigate.';
  }

  const toneClasses: Record<typeof tone, string> = {
    emerald:
      'bg-emerald-500/15 text-emerald-300 border-emerald-500/30',
    amber: 'bg-amber-500/15 text-amber-300 border-amber-500/30',
    rose: 'bg-rose-500/15 text-rose-300 border-rose-500/30',
    gray: 'bg-gray-500/15 text-gray-300 border-gray-500/30',
  };

  return (
    <div className="mt-2 flex flex-col gap-1.5">
      <div className="flex items-center gap-2 flex-wrap">
        <Badge
          className={`${toneClasses[tone]} border text-[10px] uppercase tracking-wider font-semibold`}
        >
          {icon} {label}
        </Badge>
        <Link
          href="/admin/verification"
          className="inline-flex items-center gap-1 text-[11px] text-emerald-300 hover:text-emerald-200 hover:underline underline-offset-2"
        >
          <FileCheck className="w-3 h-3" />
          View standard documents
        </Link>
      </div>
      {body && (
        <p
          className={`text-[11px] leading-snug ${
            tone === 'rose'
              ? 'text-rose-300'
              : tone === 'amber'
                ? 'text-amber-300'
                : 'text-gray-400'
          }`}
        >
          {body}
        </p>
      )}
    </div>
  );
}

function DocLink({ path, label }: { path: string | null; label: string }) {
  const [opening, setOpening] = useState(false);

  if (!path) {
    return (
      <p className="text-[11px] text-gray-600 italic mt-1.5">
        no photo uploaded
      </p>
    );
  }

  const open = async () => {
    setOpening(true);
    try {
      const { data, error } = await supabase
        .storage
        .from('driver-documents')
        .createSignedUrl(path, 60 * 5);
      if (error || !data?.signedUrl) {
        toast.error(`Could not open document: ${error?.message ?? 'no URL'}`);
        return;
      }
      window.open(data.signedUrl, '_blank', 'noopener,noreferrer');
    } catch (err: any) {
      toast.error(`Could not open document: ${err?.message ?? 'unknown'}`);
    } finally {
      setOpening(false);
    }
  };

  return (
    <button
      type="button"
      onClick={open}
      disabled={opening}
      className="mt-1.5 inline-flex items-center gap-1 text-[11px] text-emerald-300 hover:text-emerald-200 underline-offset-2 hover:underline disabled:opacity-50"
    >
      <ExternalLink className="w-3 h-3" />
      {opening ? 'Opening…' : label}
    </button>
  );
}

// ─── Destructive action dialog (Pause / Revoke) ─────────────────────
//
// One modal serves both Pause and Revoke. Internal state machine has
// three logical phases:
//
//   loading_preview → idle (preview shown) → confirming_cascade →
//     submitting → done
//
// Important behaviours:
//   • Live trip detected → red banner, "Confirm" button disabled.
//     Admin must wait it out or cancel manually first.
//   • Scheduled trips detected → confirm button stays enabled but its
//     copy escalates to "Cancel N trips & {pause|revoke}", and
//     clicking it sends p_force=true on the second submission.
//   • No commitments → simple "Pause for X days" or "Revoke" button.
//
// We DO NOT pre-flight by checking commitments client-side and
// branching to a different RPC — the server is still the source of
// truth (a trip could start between the preview and the submit) and
// always re-evaluates the rules itself. The client-side preview is
// purely informational; the server's response is what decides flow.
function DestructiveActionDialog({
  state,
  onClose,
  onSubmit,
  onSuccess,
  onUpdateState,
}: {
  state: DestructiveDialogState | null;
  onClose: () => void;
  onSubmit: (
    s: DestructiveDialogState,
    force: boolean,
  ) => Promise<{
    ok: boolean;
    error?: string;
    message?: string;
    canceled_assignments?: number;
    retracted_bids?: number;
  }>;
  onSuccess: (msg: string) => Promise<void>;
  onUpdateState: (
    updater: (cur: DestructiveDialogState) => DestructiveDialogState,
  ) => void;
}) {
  if (!state) return null;
  const isPause = state.action === 'pause';
  const labelVerb = isPause ? 'Pause' : 'Revoke';
  const labelGerund = isPause ? 'Pausing' : 'Revoking';
  const c = state.commitments;
  const liveBlocked = !!c && c.live_count > 0;
  const scheduledCascade = !!c && c.scheduled_count > 0;
  const reasonOk = state.reason.trim().length >= 5;
  const daysOk = !isPause || (state.days >= 1 && state.days <= 365);
  const canSubmit =
    !state.loadingPreview &&
    !state.previewError &&
    !liveBlocked &&
    reasonOk &&
    daysOk &&
    !state.busy;

  const handleSubmit = async () => {
    onUpdateState((cur) => ({ ...cur, busy: true }));
    try {
      // First attempt: never force. If the server reports
      // has_scheduled_charters we re-submit with force.
      let result = await onSubmit(state, false);
      if (
        !result.ok &&
        result.error === 'has_scheduled_charters' &&
        scheduledCascade
      ) {
        result = await onSubmit(state, true);
      }
      if (result.ok) {
        const parts: string[] = [];
        parts.push(
          isPause
            ? `Charter dispatch paused for ${state.days} day${state.days === 1 ? '' : 's'}.`
            : 'Charter dispatch revoked.',
        );
        if (result.canceled_assignments && result.canceled_assignments > 0) {
          parts.push(
            `Canceled ${result.canceled_assignments} scheduled trip${
              result.canceled_assignments === 1 ? '' : 's'
            } and notified the passenger${
              result.canceled_assignments === 1 ? '' : 's'
            }.`,
          );
        }
        if (result.retracted_bids && result.retracted_bids > 0) {
          parts.push(
            `Retracted ${result.retracted_bids} active bid${
              result.retracted_bids === 1 ? '' : 's'
            }.`,
          );
        }
        await onSuccess(parts.join(' '));
        return;
      }
      // Server-side hard block (e.g. live_charter_in_progress) —
      // surface its message and let the admin close the dialog.
      toast.error(
        result.message || result.error || `Could not ${labelVerb.toLowerCase()}.`,
      );
    } catch (err: any) {
      toast.error(
        `Could not ${labelVerb.toLowerCase()}: ${err?.message ?? 'unknown error'}`,
      );
    } finally {
      onUpdateState((cur) => ({ ...cur, busy: false }));
    }
  };

  return (
    <Dialog open={!!state} onOpenChange={(open) => { if (!open) onClose(); }}>
      <DialogContent className="max-w-lg bg-gray-900 border-gray-800">
        <DialogHeader>
          <DialogTitle className="text-white">
            {labelVerb} {state.driverName}?
          </DialogTitle>
          <DialogDescription className="text-gray-400">
            {isPause
              ? 'Temporarily stop this driver from receiving charter requests. They can still run corridor rides.'
              : 'Permanently strip charter eligibility. The driver returns to the application queue if they want to charter again.'}
          </DialogDescription>
        </DialogHeader>

        {/* Commitments preview */}
        <div className="space-y-3">
          <Label className="text-xs uppercase tracking-wider text-gray-500">
            Active commitments
          </Label>
          {state.loadingPreview ? (
            <div className="text-sm text-gray-500 italic">Checking commitments…</div>
          ) : state.previewError ? (
            <div className="text-sm text-red-400">
              Could not load commitments: {state.previewError}
            </div>
          ) : c ? (
            <div className="space-y-2">
              {liveBlocked ? (
                <div className="rounded-md border border-red-500/40 bg-red-500/10 p-3 space-y-2">
                  <div className="flex items-start gap-2">
                    <AlertTriangle className="w-4 h-4 text-red-400 flex-shrink-0 mt-0.5" />
                    <div className="text-sm text-red-200">
                      <div className="font-semibold">
                        Trip in progress, so you can't {labelVerb.toLowerCase()} right now.
                      </div>
                      <div className="text-xs mt-1 text-red-300/80">
                        Wait for the driver to complete or cancel the trip,
                        then try again.
                      </div>
                    </div>
                  </div>
                  {c.live.map((trip) => (
                    <div
                      key={trip.assignment_id}
                      className="text-xs text-red-200/90 pl-6"
                    >
                      • {trip.pickup_label} → {trip.dropoff_label}
                      <span className="text-red-300/60"> · {trip.status}</span>
                    </div>
                  ))}
                </div>
              ) : null}

              {scheduledCascade ? (
                <div className="rounded-md border border-amber-500/40 bg-amber-500/10 p-3 space-y-2">
                  <div className="flex items-start gap-2">
                    <CalendarDays className="w-4 h-4 text-amber-400 flex-shrink-0 mt-0.5" />
                    <div className="text-sm text-amber-200">
                      <div className="font-semibold">
                        {c.scheduled_count} scheduled charter
                        {c.scheduled_count === 1 ? '' : 's'} will be canceled.
                      </div>
                      <div className="text-xs mt-1 text-amber-300/80">
                        {labelGerund} this driver will cancel each trip and
                        send the passenger a push notification asking them to
                        rebook. No driver strike applied.
                      </div>
                    </div>
                  </div>
                  {c.scheduled.slice(0, 4).map((trip) => (
                    <div
                      key={trip.assignment_id}
                      className="text-xs text-amber-200/90 pl-6"
                    >
                      • {trip.pickup_label} → {trip.dropoff_label}
                      <span className="text-amber-300/60">
                        {' · '}
                        {new Date(trip.requested_pickup_at).toLocaleString(
                          undefined,
                          {
                            month: 'short',
                            day: 'numeric',
                            hour: 'numeric',
                            minute: '2-digit',
                          },
                        )}
                        {' · EC$'}
                        {trip.accepted_amount_xcd}
                      </span>
                    </div>
                  ))}
                  {c.scheduled.length > 4 ? (
                    <div className="text-xs text-amber-200/70 pl-6">
                      …and {c.scheduled.length - 4} more
                    </div>
                  ) : null}
                </div>
              ) : null}

              {c.active_bids_count > 0 ? (
                <div className="rounded-md border border-gray-700 bg-gray-800/50 p-3 text-xs text-gray-300 flex items-start gap-2">
                  <Banknote className="w-4 h-4 text-gray-500 flex-shrink-0 mt-0.5" />
                  <span>
                    {c.active_bids_count} active bid
                    {c.active_bids_count === 1 ? '' : 's'} on open requests
                    will be silently retracted. Passengers see one fewer
                    offer; no notification sent.
                  </span>
                </div>
              ) : null}

              {!liveBlocked && !scheduledCascade && c.active_bids_count === 0 ? (
                <div className="text-sm text-gray-500 italic">
                  No active commitments. Safe to {labelVerb.toLowerCase()}.
                </div>
              ) : null}
            </div>
          ) : null}
        </div>

        {/* Reason + days */}
        <div className="space-y-3 pt-2">
          <div className="space-y-1.5">
            <Label htmlFor="destructive-reason" className="text-xs text-gray-300">
              Reason {isPause ? '(visible to driver in their app)' : '(internal note)'}
            </Label>
            <Textarea
              id="destructive-reason"
              value={state.reason}
              onChange={(e) =>
                onUpdateState((cur) => ({ ...cur, reason: e.target.value }))
              }
              placeholder={
                isPause
                  ? 'e.g. Three rider complaints under review'
                  : 'e.g. License expired and not renewed'
              }
              className="min-h-[72px] bg-gray-950 border-gray-700 text-white placeholder:text-gray-600"
              disabled={state.busy}
            />
            <div className="text-[11px] text-gray-500">
              Required: 5+ characters. {state.reason.trim().length}/5.
            </div>
          </div>

          {isPause ? (
            <div className="space-y-1.5">
              <Label htmlFor="destructive-days" className="text-xs text-gray-300">
                Days paused
              </Label>
              <Input
                id="destructive-days"
                type="number"
                min={1}
                max={365}
                value={state.days}
                onChange={(e) =>
                  onUpdateState((cur) => ({
                    ...cur,
                    days: Math.max(1, Math.min(365, Number(e.target.value) || 14)),
                  }))
                }
                className="w-32 bg-gray-950 border-gray-700 text-white"
                disabled={state.busy}
              />
              <div className="text-[11px] text-gray-500">
                1–365 days. 14 days matches the auto 3-strike pause.
              </div>
            </div>
          ) : null}
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={onClose}
            disabled={state.busy}
            className="border-gray-700 text-gray-300"
          >
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={!canSubmit}
            variant={isPause ? 'default' : 'destructive'}
            className={
              isPause
                ? 'bg-amber-500 hover:bg-amber-600 text-gray-950'
                : undefined
            }
          >
            {state.busy
              ? `${labelGerund}…`
              : scheduledCascade
                ? `Cancel ${c!.scheduled_count} trip${c!.scheduled_count === 1 ? '' : 's'} & ${labelVerb.toLowerCase()}`
                : isPause
                  ? `Pause for ${state.days} day${state.days === 1 ? '' : 's'}`
                  : 'Revoke charter access'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
